/** Demo Mode only - Finance > Contracts > Contract detail drill-down. Financial summary
 * tiles, monthly revenue/GP/EBIT trend, contract P&L via the shared P&L component,
 * actual vs budget vs forecast, key variances, and finance commentary/flagged risks. */

import { useEffect } from 'react';
import { Box, Breadcrumbs, Button, Grid, Link, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { AttachMoney, Restaurant, Assessment, Flag } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Section } from '../../../components/ui/Section';
import { applyRoundAndFormat } from '../../../utils';
import useCurrencyCodeConfiguration from '../../../hooks/configurations/useCurrencyCodeConfiguration';
import { FINANCE_CONTRACTS, classifyFinancialStatus, computeTotals } from '../../../mocks/data/financeData';
import MetricTile from '../components/MetricTile';
import TrendChart from '../components/TrendChart';
import ProfitAndLossTable from '../components/ProfitAndLossTable';
import FinanceExceptionCards from '../components/FinanceExceptionCards';
import { evaluateFinanceExceptions } from '../financeExceptions';

const ContractDetail = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { contractId } = useParams<{ contractId: string }>();
  const { currencyCode, getCurrencyCodeConfiguration } = useCurrencyCodeConfiguration();

  useEffect(() => {
    getCurrencyCodeConfiguration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contract = FINANCE_CONTRACTS.find((c) => c.id === contractId);

  if (!contract) {
    return (
      <Section style={{ p: 4 }}>
        <Typography variant="textMD">{t('financeContractNotFound', 'Contract not found.')}</Typography>
        <Button variant="secondary"
          onClick={() => navigate('/finance-performance?tab=contracts')}
          sx={{ mt: 2 }}>
          {t('financeBackToContracts', 'Back to contracts')}
        </Button>
      </Section>
    );
  }

  const formatCurrency = (value: number) => (value < 0 ? `(${applyRoundAndFormat(Math.abs(value), currencyCode)})` : applyRoundAndFormat(value, currencyCode));
  const budget = computeTotals(contract.scenarios.budget);
  const forecast = computeTotals(contract.scenarios.currentForecast);
  const status = classifyFinancialStatus(contract);
  const exceptions = evaluateFinanceExceptions([contract], formatCurrency);

  const tiles = [
    { key: 'revenue', label: t('financeOverviewRevenue', 'Revenue'), value: formatCurrency(forecast.totalRevenue), subtitle: `${t('financeOverviewVsBudget', 'vs budget')} ${formatCurrency(budget.totalRevenue)}`, icon: <AttachMoney />, color: '#1976d2' },
    { key: 'grossProfit', label: t('financePnlGrossProfit', 'Gross profit'), value: formatCurrency(forecast.grossProfit), subtitle: `${forecast.grossProfitPercentage.toFixed(1)}% GP`, icon: <Restaurant />, color: '#2e7d32' },
    { key: 'ebit', label: t('financePnlEbit', 'EBIT'), value: formatCurrency(forecast.ebit), subtitle: `${forecast.ebitMarginPercentage.toFixed(1)}% margin`, icon: <Assessment />, color: forecast.ebit >= budget.ebit ? '#2e7d32' : '#d32f2f' },
    { key: 'status', label: t('financeContractsFinancialStatus', 'Financial status'), value: t(`financeStatus${status.replace(/\s/g, '')}`, status), subtitle: contract.situation, icon: <Flag />, color: '#9c27b0' },
  ];

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link component="button"
          underline="hover"
          onClick={() => navigate('/finance-performance?tab=contracts')}>
          {t('financeContractsTableTitle', 'Contract performance')}
        </Link>
        <Typography variant="textSM"
          color="text.primary">{contract.contractName}</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <Button variant="secondary"
          size="md"
          onClick={() => navigate('/finance-performance?tab=contracts')}>
          <ArrowBackIcon fontSize="small" />
        </Button>
        <Box>
          <Typography variant="textXL"
            fontWeight={600}>{contract.contractName}</Typography>
          <Typography variant="textSM"
            color="text.secondary">{`${contract.client} \u2022 ${contract.site} \u2022 ${contract.sector}`}</Typography>
        </Box>
      </Box>

      <Grid container
        spacing={4}
        sx={{ mb: 6 }}>
        {tiles.map((tile) => (
          <Grid item
            xs={12}
            sm={6}
            md={3}
            key={tile.key}>
            <MetricTile {...tile} />
          </Grid>
        ))}
      </Grid>

      <Section style={{ p: 4, mb: 6 }}>
        <TrendChart
          title={t('financeContractDetailTrendTitle', 'Monthly EBIT - actual/forecast vs budget')}
          series={[
            { label: t('financePnlCurrentForecast', 'Actual + current forecast'), data: contract.blendedTrend.ebit, color: '#1976d2' },
            { label: t('financePnlBudget', 'Budget'), data: contract.monthly.budget.ebit, color: '#9e9e9e', dashed: true },
          ]}
          valueFormatter={(v) => (v === null ? '' : applyRoundAndFormat(v, currencyCode))}
        />
      </Section>

      <Section style={{ p: 4, mb: 6 }}>
        <Typography variant="textMD"
          fontWeight={600}
          sx={{ mb: 2 }}>
          {t('financeContractDetailCommentary', 'Finance commentary')}
        </Typography>
        <Typography variant="textSM"
          color="text.secondary">
          {t('financeContractDetailCommentaryText', '{{situation}} - current forecast EBIT of {{ebit}} against a budget of {{budget}} ({{margin}}% margin).', {
            situation: contract.situation, ebit: formatCurrency(forecast.ebit), budget: formatCurrency(budget.ebit), margin: forecast.ebitMarginPercentage.toFixed(1),
          })}
        </Typography>
      </Section>

      <Section style={{ p: 4, mb: 6 }}>
        <Typography variant="textMD"
          fontWeight={600}
          sx={{ mb: 2 }}>
          {t('financeOverviewExceptions', 'Finance exceptions')}
        </Typography>
        <FinanceExceptionCards exceptions={exceptions}
          emptyMessage={t('financeOverviewNoExceptions', 'No finance exceptions in scope.')} />
      </Section>

      <Section style={{ p: 4 }}>
        <Typography variant="textMD"
          fontWeight={600}
          sx={{ mb: 3 }}>
          {t('profitAndLossTitle', 'Profit & Loss')}
        </Typography>
        <ProfitAndLossTable contracts={[contract]}
          view="fullYear"
          currencyCode={currencyCode} />
      </Section>
    </Box>
  );
};

export default ContractDetail;
